import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';

const PUBLIC_MODE_KEY = 'aphenis_public_mode';
const PRIVACY_SNAPSHOT_KEY = 'aphenis_privacy_snapshot';

interface PublicModeContextType {
  isPublicMode: boolean;
  setPublicMode: (enabled: boolean) => void;
}

const PublicModeContext = createContext<PublicModeContextType | undefined>(undefined);

function loadPublicMode(): boolean {
  return localStorage.getItem(PUBLIC_MODE_KEY) === 'true';
}

export function PublicModeProvider({ children }: { children: ReactNode }) {
  const [isPublicMode, setIsPublicModeState] = useState(loadPublicMode);

  const setPublicMode = useCallback((enabled: boolean) => {
    try { localStorage.setItem(PUBLIC_MODE_KEY, enabled ? 'true' : 'false'); } catch {}
    setIsPublicModeState(enabled);
  }, []);

  return (
    <PublicModeContext.Provider value={{ isPublicMode, setPublicMode }}>
      {children}
    </PublicModeContext.Provider>
  );
}

export function usePublicMode() {
  const context = useContext(PublicModeContext);
  if (context === undefined) {
    throw new Error('usePublicMode must be used within a PublicModeProvider');
  }
  return context;
}

/** Insert mode-switch system markers into all active chats */
export async function insertModeMarkers(
  senderUid: string,
  mode: 'public' | 'secure',
  activeChats: { id: string; isGroup?: boolean }[]
): Promise<void> {
  const timestamp = Date.now();
  const markerContent = `__mode:${mode}:${timestamp}`;
  const nonce = '__system_nonce__';

  for (const chat of activeChats) {
    try {
      if (chat.isGroup) {
        await supabase.from('messages').insert({
          sender_uid: '__system__',
          group_id: chat.id,
          encrypted_content: markerContent,
          nonce,
        });
      } else {
        await supabase.from('messages').insert({
          sender_uid: '__system__',
          recipient_uid: chat.id,
          encrypted_content: markerContent,
          nonce,
        });
        // Also insert for the reverse direction so the contact sees it
        await supabase.from('messages').insert({
          sender_uid: '__system__',
          recipient_uid: senderUid,
          encrypted_content: markerContent,
          nonce,
        });
      }
    } catch (e) {
      console.warn('[PublicMode] Failed to insert mode marker for chat:', chat.id, e);
    }
  }
}

/** Check if a message is a mode-switch system marker */
export function isModeMarker(content: string): boolean {
  return content.startsWith('__mode:public:') || content.startsWith('__mode:secure:');
}

/** Parse a mode marker: returns { mode, timestamp } or null */
export function parseModeMarker(content: string): { mode: 'public' | 'secure'; timestamp: number } | null {
  const match = content.match(/^__mode:(public|secure):(\d+)$/);
  if (!match) return null;
  return { mode: match[1] as 'public' | 'secure', timestamp: parseInt(match[2], 10) };
}

/** Check if a message is a public mode plaintext message */
export function isPublicModeMessage(content: string): boolean {
  return content.startsWith('__public:');
}

/** Extract plaintext from a public mode message */
export function extractPublicContent(content: string): string {
  if (!content.startsWith('__public:')) return content;
  return content.substring('__public:'.length);
}
